// Aggregation for the Executive tab charts. Every function here is a pure function of its arguments:
// nothing reads `Date.now()` or any other global, because the page supports a historical `as_of` and
// "today" must always come from the payload. Month-end figures are read through `monthStat` so these
// charts cannot drift away from the Monthly view by team table, which uses the same reducer.

import { monthStat } from './monthly'
import type { PayloadSnapshot, Row, Tier } from './types'

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export interface MonthlyPoint {
  /** Month-end date, exactly as it appears in `payload.months`. */
  month: string
  /** Short display label, for example `Aug 2026`. */
  label: string
  open: number
  overdue: number
  /** Open at month end and not overdue then. `openOnTime + overdue === open`. */
  openOnTime: number
  /** Paid down during that month. */
  resolved: number
  /** Registered by then, but with a state we could not reconstruct. */
  unknown: number
}

export type TierMixPoint = {
  month: string
  label: string
  /** Open items at month end, across every tier. */
  total: number
} & Record<Tier, number>

export type AreaRiskPoint = {
  /** Platform area, taken from `row.svp`. */
  area: string
  total: number
  overdue: number
  /** Severity weight used for ordering. Higher means more severe risk. */
  weight: number
} & Record<Tier, number>

export interface TrajectoryVerdict {
  direction: 'up' | 'down' | 'flat'
  first: number
  last: number
  delta: number
  firstLabel: string
  lastLabel: string
}

type MonthlyPayload = Pick<PayloadSnapshot, 'months' | 'mbits'>
type TierPayload = MonthlyPayload & Pick<PayloadSnapshot, 'tier_order'>
type AreaPayload = Pick<PayloadSnapshot, 'tier_order'>

/** Turns a month-end date into a short label, for example `2026-08-21` into `Aug 2026`. */
export function monthLabel(month: string): string {
  const name = MONTH_NAMES[Number(month.slice(5, 7)) - 1]
  return name ? `${name} ${month.slice(0, 4)}` : month
}

function usableMonths(payload: MonthlyPayload): string[] {
  const bits = payload.mbits
  if (!bits || typeof bits.reg !== 'number' || typeof bits.open !== 'number') return []
  return Array.isArray(payload.months) ? payload.months : []
}

function zeroTiers(tiers: Tier[]): Record<Tier, number> {
  const counts = {} as Record<Tier, number>
  tiers.forEach((tier) => { counts[tier] = 0 })
  return counts
}

/** Severity weight for a tier, derived from its position in `tier_order` so the payload stays in charge. */
function tierWeight(tiers: Tier[], tier: Tier): number {
  const position = tiers.indexOf(tier)
  return position === -1 ? 0 : tiers.length - position
}

/** One entry per month in `payload.months`, in the same order, built on `monthStat`. */
export function monthlySeries(rows: Row[], payload: MonthlyPayload): MonthlyPoint[] {
  return usableMonths(payload).map((month, index) => {
    const stat = monthStat(rows, index, payload.mbits)
    return {
      month,
      label: monthLabel(month),
      open: stat.vol,
      overdue: stat.over,
      openOnTime: Math.max(stat.vol - stat.over, 0),
      resolved: stat.paid,
      unknown: stat.unknown,
    }
  })
}

/**
 * Open items per month, split by tier. `row.tier` is the item's current tier, not a historical one, so
 * the mix for past months is indicative rather than exact. Callers must say so on screen.
 */
export function tierMixSeries(rows: Row[], payload: TierPayload): TierMixPoint[] {
  const tiers = payload.tier_order || []
  const bits = payload.mbits
  return usableMonths(payload).map((month, index) => {
    const counts = zeroTiers(tiers)
    let total = 0
    rows.forEach((row) => {
      const state = row.months[index] || 0
      if (!(state & bits.reg) || !(state & bits.open)) return
      total++
      if (row.tier in counts) counts[row.tier]++
    })
    return { month, label: monthLabel(month), total, ...counts }
  })
}

/**
 * Currently open risk per platform area, with a tier breakdown and an overdue count. Ordered by
 * severity weight, then total, then area name, so the same rows always render in the same order.
 */
export function areaRiskSeries(rows: Row[], payload: AreaPayload): AreaRiskPoint[] {
  const tiers = payload.tier_order || []
  const areas = new Map<string, AreaRiskPoint>()
  rows.forEach((row) => {
    if (!row.open) return
    const area = row.svp
    const entry = areas.get(area) || { area, total: 0, overdue: 0, weight: 0, ...zeroTiers(tiers) }
    entry.total++
    if (row.due_status === 'overdue') entry.overdue++
    if (row.tier in entry) {
      entry[row.tier]++
      entry.weight += tierWeight(tiers, row.tier)
    }
    areas.set(area, entry)
  })
  return Array.from(areas.values()).sort(
    (left, right) => right.weight - left.weight || right.total - left.total || left.area.localeCompare(right.area),
  )
}

export const CHART_WINDOWS = ['30d', '60d', '90d', '180d', '365d', 'ytd'] as const

export type ChartWindow = typeof CHART_WINDOWS[number]

export const CHART_WINDOW_LABELS: Record<ChartWindow, string> = {
  '30d': 'Last 30 days',
  '60d': 'Last 60 days',
  '90d': 'Last 90 days',
  '180d': 'Last 180 days',
  '365d': 'Last 365 days',
  ytd: 'Year to date',
}

// These charts are built from month-end reconstruction, so a day window can only ever resolve to a
// whole number of trailing months. 30 days is one month end, 365 days is twelve. The server caps the
// reconstruction at 12 months (MONTHS_BACK), and a young register holds fewer months than that, so a
// window is routinely wider than the history behind it. `windowCoverage` reports when that happens so
// the chart can say so rather than implying the window was honoured.
const WINDOW_MONTHS: Record<Exclude<ChartWindow, 'ytd'>, number> = {
  '30d': 1,
  '60d': 2,
  '90d': 3,
  '180d': 6,
  '365d': 12,
}

export function isChartWindow(value: unknown): value is ChartWindow {
  return CHART_WINDOWS.includes(value as ChartWindow)
}

export interface WindowCoverage {
  /** Month-end points actually shown. */
  shown: number
  /** Month-end points the window asked for. */
  requested: number
  /** True when the register holds less history than the window covers. */
  clamped: boolean
}

/**
 * Month-end points a window covers, before any clamp to available history. Year to date is January
 * through the `as_of` month, so its month count is just the `as_of` month number.
 */
function requestedMonths(window: ChartWindow, asOf: string): number {
  if (window !== 'ytd') return WINDOW_MONTHS[window]
  return Math.max(1, Number(asOf.slice(5, 7)))
}

/** Trims a month-end series to the trailing window, clamped to the history that exists. */
export function sliceMonths<T extends { month: string }>(series: T[], window: ChartWindow, asOf: string): T[] {
  if (!series.length) return series
  if (window === 'ytd') {
    const yearStart = `${asOf.slice(0, 4)}-01-01`
    return series.filter((point) => point.month >= yearStart)
  }
  return series.slice(-WINDOW_MONTHS[window])
}

/** How much of the requested window the register can actually cover. */
export function windowCoverage<T extends { month: string }>(
  series: T[],
  window: ChartWindow,
  asOf: string,
): WindowCoverage {
  const shown = sliceMonths(series, window, asOf).length
  const requested = requestedMonths(window, asOf)
  return { shown, requested, clamped: shown < requested }
}

/** Compares open risk at the start and end of the month window. */
export function trajectoryVerdict(series: MonthlyPoint[]): TrajectoryVerdict {
  if (!series.length) {
    return { direction: 'flat', first: 0, last: 0, delta: 0, firstLabel: '', lastLabel: '' }
  }
  const first = series[0]
  const last = series[series.length - 1]
  const delta = last.open - first.open
  return {
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
    first: first.open,
    last: last.open,
    delta,
    firstLabel: first.label,
    lastLabel: last.label,
  }
}
